import { MutableRefObject } from 'react'
import moment from 'moment'
import { TDateFormat } from 'models/shared.models'
import { CatalogueEntry } from 'models/catalogues'
import { dateConfigOptionsMap } from 'lib/utils/shared.utils'

export const deepMerge = <T extends Record<string, any>>(
  target: T,
  source: T
): T => {
  for (const key in source) {
    if (source[key] && typeof source[key] === 'object') {
      if (!target[key] || typeof target[key] !== 'object') {
        target[key] = Array.isArray(source[key]) ? [] : ({} as any)
      }
      deepMerge(target[key], source[key])
    } else {
      target[key] = source[key]
    }
  }
  return target
}

export const capitalize = (str: string): string => {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const compareSortByMultipleKeys = (
  a: any,
  b: any,
  keys: string[],
  orders: string[]
) => {
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    const order = orders[i]
    if (a[key] < b[key]) return order === 'asc' ? -1 : 1
    if (a[key] > b[key]) return order === 'asc' ? 1 : -1
  }
  return 0
}

export const throttle = <T extends (...args: any[]) => Promise<any>>(
  func: T,
  wait: number
) => {
  let timeout: ReturnType<typeof setTimeout> | null = null
  let pendingPromise: Promise<any> | null = null

  const throttledFunction = function (
    this: any,
    ...args: Parameters<T>
  ): Promise<any> {
    if (!timeout) {
      timeout = setTimeout(() => {
        timeout = null
        pendingPromise = null
      }, wait)

      pendingPromise = func.apply(this, args)
    }

    return pendingPromise ?? Promise.resolve()
  }

  throttledFunction.cancel = () => {
    if (timeout) {
      clearTimeout(timeout)
      timeout = null
      pendingPromise = null
    }
  }

  return throttledFunction
}

export const fileToBase64 = async (file: File): Promise<string> => {
  try {
    const response = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        const result: string = reader.result as string
        const base64String: string = (result as string).substring(
          result.indexOf(',') + 1,
          result.length
        )
        resolve(base64String)
      }
      reader.onerror = () => resolve('')
    })
    return response as string
  } catch (error) {
    return ''
  }
}

export const toggleChatComponent = (open: boolean) => {
  open
    ? document.body.classList.remove('no-chat')
    : document.body.classList.add('no-chat')
}

export const animateDetailsHeight = (
  animationRef: MutableRefObject<Animation | null>,
  detailsRef: MutableRefObject<HTMLDetailsElement | null>,
  endHeight: string
) => {
  if (!detailsRef.current) return

  const startHeight = `${detailsRef.current.offsetHeight}px`

  if (animationRef.current) {
    animationRef.current.cancel()
  }

  animationRef.current = detailsRef.current.animate(
    {
      height: [startHeight, endHeight],
    },
    {
      duration: 300,
      easing: 'ease-out',
    }
  ) as Animation | null
}

export const getSafeDecimalValue = (value?: string | number) =>
  parseFloat(value as string) || 0.0

export const getFormattedTime = (
  dateString?: string,
  hour12 = false,
  fallback: string = '---'
): string => {
  if (!dateString || isNaN(Date.parse(dateString))) {
    return fallback
  }

  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12,
    timeZone: 'America/Mexico_City',
  }

  const date = new Date(dateString)
  let formattedTime = new Intl.DateTimeFormat('es-MX', options).format(date)

  if (hour12) {
    formattedTime = formattedTime
      .replace(/\s+/g, ' ')
      .replace(/ a\. m\./i, ' am')
      .replace(/ p\. m\./i, ' pm')
  }

  return formattedTime
}

export const getFormattedDate = (
  dateString?: string,
  formatType: TDateFormat = 'short',
  fallback: string = '---'
): string => {
  if (!dateString || isNaN(Date.parse(dateString))) {
    return fallback
  }

  if(dateString.match(/^\d{4}-\d{2}-\d{2}$/)){
    // Parse dateString as a Date, regarding browser timezone offset first
    let localDate = new Date(dateString);
    localDate = new Date(localDate.getTime() + localDate.getTimezoneOffset() * 60000);
    dateString = localDate.toISOString();
  }

  const options: Intl.DateTimeFormatOptions = dateConfigOptionsMap[formatType]

  const date = new Date(dateString)
  let formattedDate = new Intl.DateTimeFormat('es-MX', options).format(date)

  if (formatType === 'monthYear') {
    formattedDate = capitalize(formattedDate)
  }

  return formattedDate
}

/* 
   The formatDateToSpanish helper was created to handle a specific input format (12/Jul 2024) that's not
   accepted as valid by the native Date constructor, on top of the month being in english. Updating endpoint
   response to ISO8601 format is the best solution, but this helper is a workaround for now.
*/

export const formatDateToSpanish = (
  inputDate: string,
  inputFormat: string,
  outputformat?: string
): string => {
  const date = moment(inputDate)
  date.locale('es')
  return date.format(outputformat ?? inputFormat).replace('.', '')
}

export const getFormattedCurrency = (
  amount?: number,
  currency: string = 'MXN',
  fallback: string = '---'
): string => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return fallback
  }
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'narrowSymbol',
  }).format(amount)
}

export const renderCatalogueOption = (o: CatalogueEntry) => (
  <span className="catalogue-option">{`${o.value} - ${o.label}`}</span>
)

export const formatCatalogueLabel = (type?: CatalogueEntry) => {
  if (!type) return '---'
  return `${type.value} - ${type.label}`
}

export const getUpdatedURL = (
  params: URLSearchParams,
  paramToDelete: string,
  pathname: string
) => {
  const paramsCopy = new URLSearchParams(params)
  paramsCopy.delete(paramToDelete)
  const newSearch = paramsCopy.toString()
  return newSearch ? `${pathname}?${newSearch}` : pathname
}

export const deepEqual = (obj1: any, obj2: any, omitKeys?: string[]) => {
  if (
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object' ||
    obj1 === null ||
    obj2 === null
  ) {
    return obj1 === obj2
  }

  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  if (keys1.length !== keys2.length) {
    return false
  }

  for (const key of keys1) {
    if (omitKeys?.includes(key)) {
      continue
    }

    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key], omitKeys)) {
      return false
    }
  }

  return true
}
